

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Col, Row } from "antd";
import { Dialog } from "primereact/dialog";
import { Backdrop, CircularProgress } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import Diagno from "../../Assets/Images/diagno_lab.svg";
import SmartReportUploadViewer from "../SmartReport/SmartReportUpload";

const PAGE_SIZE = 6;

export default function SmartReportLabList({ onClose = () => {} }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedReportData, setSelectedReportData] = useState(null);
  const [generateSmart, setGenerateSmart] = useState(false);
  const [labReportList, setLabReport] = useState([]);
  const [expandedItems, setExpandedItems] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [currentPage, setCurrentPage] = useState(1);

  const storedData = JSON.parse(localStorage.getItem("loginObj"));
  const userData = storedData;

  useEffect(() => {
    const fetchReports = async () => {
      if (!userData?.code) return;
      try {
        setLoading(true);
        const res = await fetch(
          `${process.env.REACT_APP_SMART_REPORT_BASEURL}get_smart_report_request?patient_id=${userData?.code}`
        );
        const json = await res.json();
        setLabReport(json?.data || []);
      } catch (error) {
        console.error("Error fetching smart reports:", error);
        toast.error("No Smart Report Found");
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, [userData?.code]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const generateSmartReport = async (orderId) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SMART_REPORT_BASEURL}/get_smart_report` +
          `?orderid=${orderId}` +
          `&patientid=${userData?.code}` +
          `&sourcechannel=B2C` +
          `&createdby=${userData?.code}` +
          `&eclinic_code=CENTRALCLINIC` +
          `&request_type=response` +
          `&page=1` +
          `&page_size=100`
      );
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = await res.json();
      if (isMobile) {
        navigate(
          `/smartreportupload?orderId=${encodeURIComponent(orderId)}`,
          { state: { reportData: data } }
        );
        return;
      }
      setSelectedReportData(data);
      setGenerateSmart(true);
    } catch (err) {
      console.error("fetch-response error:", err);
      toast.error("Unable to load report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (globalIdx) => {
    setExpandedItems((prev) => {
      const updated = [...prev];
      updated[globalIdx] = !updated[globalIdx];
      return updated;
    });
  };

  const filteredList = labReportList.filter((res) => res.orderId?.length > 0);
  const totalPages = Math.ceil(filteredList.length / PAGE_SIZE);
  const paginatedList = filteredList.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <ToastContainer />
      <Backdrop
        sx={{ color: "#5579C6", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <section className="home-banner bg-lightGray md:min-h-[80vh] py-8 relative overflow-hidden">
        <div className="w-[80%] mx-auto">
          <div
            className="w-full rounded-md md:px-8 px-6 md:py-5 py-3"
            style={{
              background:
                "linear-gradient(282.08deg, #DFF1FB 2.51%, #FFFFFF 100.3%)",
            }}
          >
            <div className="md:flex justify-between items-center">
              <span
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                  fontSize: "24px",
                  lineHeight: "100%",
                  letterSpacing: "0.3%",
                  verticalAlign: "middle",
                  color: "var(--color-brand-primary)",
                  display: "inline-block",
                }}
              >
                My Smart Reports
              </span>
              {filteredList.length > 0 && (
                <span className="text-sm text-gray-500 mt-1 md:mt-0">
                  {filteredList.length} report{filteredList.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          </div>

          {paginatedList.length > 0 ? (
            <>
              <Row gutter={24} className="mt-5">
                {paginatedList.map((lab, localIdx) => {
                  const globalIdx = (currentPage - 1) * PAGE_SIZE + localIdx;
                  const isExpanded = expandedItems[globalIdx];
                  const maxChars = 100;
                  const testDetail = lab.test_detail || "";
                  const displayText = isExpanded
                    ? testDetail
                    : testDetail.length > maxChars
                    ? testDetail.slice(0, maxChars) + "..."
                    : testDetail;
                  const isVerified = lab.bo_verified === "1";

                  return (
                    <Col xs={24} sm={24} md={12} lg={8} key={globalIdx}>
                      <div className="bg-white rounded-md p-4 shadow hover:shadow-lg transition-shadow mb-2">
                        <div className="flex items-center mb-2">
                          <img src={Diagno} alt="Diagno" className="w-10 h-10 mr-2" />
                          <div className="text-base font-semibold text-gray-700">
                            {lab.patient_name}
                          </div>
                        </div>

                        <p className="text-sm text-gray-600">
                          <span className="font-semibold">Order ID:</span> {lab.orderId}
                        </p>

                        <p className="text-sm text-gray-600 mb-4">
                          <span className="font-semibold">Test Detail:</span>{" "}
                          {displayText}
                          {testDetail.length > maxChars && (
                            <button
                              onClick={() => toggleExpand(globalIdx)}
                              className="ml-2 text-secondaryColor font-bold"
                            >
                              {isExpanded ? "Read Less" : "Read More"}
                            </button>
                          )}
                        </p>

                        <p className="text-sm text-gray-600 mb-4">
                          <span className="font-semibold">Test Date:</span> {lab.test_date}
                        </p>

                        <p className="text-sm text-gray-600 mb-4">
                          <span className="font-semibold">Created Date:</span> {lab.createdDate}
                        </p>

                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => {
                              if (isVerified) {
                                generateSmartReport(lab.orderId);
                              } else {
                                toast.info(
                                  "Thank you for your patience! We're still verifying your smart report."
                                );
                              }
                            }}
                            className={`px-4 py-2 rounded text-white ${
                              isVerified
                                ? "bg-[#004276] hover:bg-[#003360]"
                                : "bg-[#004276] opacity-50 cursor-not-allowed"
                            }`}
                          >
                            View Smart Report
                          </button>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>

              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-8">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded border text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ borderColor: "var(--color-brand-primary)", color: "var(--color-brand-primary)" }}
                  >
                    Prev
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className="w-8 h-8 rounded text-sm font-medium"
                      style={
                        page === currentPage
                          ? { background: "var(--color-brand-primary)", color: "#fff" }
                          : { border: "1px solid var(--color-brand-primary)", color: "var(--color-brand-primary)" }
                      }
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded border text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ borderColor: "var(--color-brand-primary)", color: "var(--color-brand-primary)" }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="flex justify-center items-center mt-12">
              <p className="text-lg text-gray-500">No Smart Reports Found.</p>
            </div>
          )}
        </div>
      </section>

      {!isMobile && generateSmart && (
        <Dialog
          visible={generateSmart}
          onHide={() => setGenerateSmart(false)}
          className="w-6/12 p-0"
          draggable={false}
          resizable={false}
          header="Smart Report"
        >
          <SmartReportUploadViewer reportData={selectedReportData} />
        </Dialog>
      )}
    </>
  );
}
